import { IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceDataConfigurationForm, ISourceDataConfigurationFormField, ISourceDataConfigurationFormFieldValue, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion } from "@/api/dtos";
import { ConfigurationType, ISourceDataConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";
import { IconDefinition } from "@fortawesome/pro-duotone-svg-icons";

export const CSharpTypeMapping: Record<ConfigurationType, string> = {
    [ConfigurationType.Event]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationEvent, NewtonGreen.ServiceStack.Sdv.Interfaces',
    [ConfigurationType.Form]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationForm, NewtonGreen.ServiceStack.Sdv.Interfaces',
    [ConfigurationType.Version]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationFormVersion, NewtonGreen.ServiceStack.Sdv.Interfaces',
    [ConfigurationType.Section]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationFormSection, NewtonGreen.ServiceStack.Sdv.Interfaces',
    [ConfigurationType.Field]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationFormField, NewtonGreen.ServiceStack.Sdv.Interfaces',
    [ConfigurationType.Value]: 'NewtonGreen.ServiceStack.Sdv.Models.Data.SourceDataConfigurationFormFieldValue, NewtonGreen.ServiceStack.Sdv.Interfaces'
}

export interface ISelectionOption {
    name: string;
    label: string;
    options?: ISelectionOption[];
    disabled?: boolean;
    update?: (source: IConfigurationFormEditUpdateItem, target: IConfigurationFormEditUpdateItem, sourceValue: ISourceDataConfigurationItem, targetValue: ISourceDataConfigurationItem, newValue: ISourceDataConfigurationItem) => void;
}

export interface IConfigurationFormEditUpdateItem {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey?: string;
    formMetadata?: IFormMetadata;
    formVersionKey?: string;
    formVersionMetadata?: IFormVersionMetadata;
    formSectionKey?: string;
    formSectionMetadata?: IFormSectionMetadata;
    formFieldKey?: string;
    formFieldMetadata?: IFormFieldMetadata;
    formFieldTypeLabel?: string;
    formFieldValueKey?: string;
    formFieldValueMetadata?: IFormFieldValueMetadata;
    configurationPropertyPath?: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

export interface IUpdateMapping {
    createOptions: (source: IConfigurationFormEditUpdateItem, target: IConfigurationFormEditUpdateItem) => ISelectionOption[];
    update: (
        source: IConfigurationFormEditUpdateItem, 
        target: IConfigurationFormEditUpdateItem, 
        sourceValue: ISourceDataConfigurationItem, 
        targetValue: ISourceDataConfigurationItem, 
        options: ISelectionOption[],
        selection: any
    ) => ISourceDataConfigurationItem;
}

export const updateBySelection = (
    source: IConfigurationFormEditUpdateItem, 
    target: IConfigurationFormEditUpdateItem, 
    sourceValue: ISourceDataConfigurationItem, 
    targetValue: ISourceDataConfigurationItem, 
    newValue: ISourceDataConfigurationItem, 
    options: ISelectionOption[], 
    selection: any
) => {
    let total = 0;
    let checked = 0;

    for (let option of options) {
        if (selection[option.name] && option.update) {
            option.update(source, target, sourceValue, targetValue, newValue);
        }

        if (option.options && option.options.length > 0) {
            updateBySelection(source, target, sourceValue, targetValue, newValue, option.options, selection[option.name]);
        }
        else if (selection[option.name] && option.update) {
            option.update(source, target, sourceValue, targetValue, newValue);
        }
    }

    return {
        total,
        checked
    };
}



export const createVersions = (source: ISourceDataConfigurationForm, target: ISourceDataConfigurationForm | undefined, newItem: ISourceDataConfigurationForm, targetMetadata?: IFormMetadata) => {
    const versions = targetMetadata?.versions?.filter(x => !x.formMetadataId) ?? [];

    if (newItem.linked !== (target?.linked ?? true) && (!newItem.versions || Object.keys(newItem.versions).length === 0)  && versions.length > 0) {
        newItem.versions = {};

        for (let valueMetadata of versions) {
            newItem.versions![valueMetadata.id!] = {
                __type: CSharpTypeMapping[ConfigurationType.Version], 
                formVersionMetadataId: valueMetadata.id!,
                linked: true,
                enabled: true
            } as ISourceDataConfigurationForm 
        }
    }
}


export const updateVersion = (source: ISourceDataConfigurationForm, target: ISourceDataConfigurationForm,  newItem: ISourceDataConfigurationForm, key: string, targetMetadata?: IFormMetadata) => {
    if (!newItem.versions) {
        return;
    }
    
    let newChildItem = newItem.versions[key];

    
    if (!newChildItem) {
        return;
    }
    
    const {
        __type: currentType,
        id: currentId, 
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormId: currentFormId,
        formVersionMetadataId: currentFormVersionMetadataId,
        linked: currentLinked,
        sections: currentSectionCollection,
        fields: currentFieldCollection,
        ...currentValues
    } = newChildItem as ISourceDataConfigurationFormVersion & { __type: string };

    const {
        __type: copyType,
        id: copyId,
        enteredBy: copyEnteredBy,
        enteredDate: copyEnteredDate,
        modifiedBy: copyModifiedBy,
        modifiedDate: copyModifiedDate,
        sourceDataConfigurationFormId: copyFormId,
        formVersionMetadataId: copyFormVersionMetadataId,
        linked: copyLinked, 
        sections: copySectionCollection,
        fields: copyFieldCollection,
        ...copyValues
    } = source.versions![key] as ISourceDataConfigurationFormVersion & { __type: string };

    newItem.versions[key] = {
        __type: currentType, 
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormId: currentFormId,
        formVersionMetadataId: currentFormVersionMetadataId,
        sections: {...currentSectionCollection},
        fields: {...currentFieldCollection},
        linked: copyLinked, 
        ...copyValues
    } as ISourceDataConfigurationFormVersion;

    const targetVersionMetadata = targetMetadata?.versions?.find(y => y.id === key);

    updateSections(source.versions![key], target.versions?.[key], newItem.versions![key], targetVersionMetadata);
    updateVersionFields(source.versions![key], target.versions?.[key], newItem.versions![key], targetVersionMetadata)
}

export const createVersionFields = (source: ISourceDataConfigurationFormVersion, target: ISourceDataConfigurationFormVersion | undefined, newItem: ISourceDataConfigurationFormVersion, targetVersionMetadata?: IFormVersionMetadata) => {
    const fields = targetVersionMetadata?.fields?.filter(x => !x.formSectionMetadataId) ?? [];

    if (newItem.linked !== (target?.linked ?? true) && (!newItem.fields || Object.keys(newItem.fields).length === 0)  && fields.length > 0) {
        newItem.fields = {};

        for (let valueMetadata of fields) {
            newItem.fields![valueMetadata.id!] = {
                __type: CSharpTypeMapping[ConfigurationType.Field], 
                formFieldMetadataId: valueMetadata.id!,
                linked: true,
                enabled: true
            } as ISourceDataConfigurationFormField 
        }
    }
}

export const updateVersionFields = (source: ISourceDataConfigurationFormVersion, target: ISourceDataConfigurationFormVersion | undefined, newItem: ISourceDataConfigurationFormVersion, targetVersionMetadata?: IFormVersionMetadata) => {
    if (!newItem.linked) {
        createVersionFields(source, target, newItem, targetVersionMetadata);

        if (source.fields) {
            for (let fieldKey of Object.keys(source.fields)) {
                updateField(
                    source,
                    target,
                    newItem,
                    fieldKey,
                    targetVersionMetadata
                )
            }
        }
    }
    else if (newItem.linked) {
        newItem.fields = undefined;
    }
}

export const createSections = (source: ISourceDataConfigurationFormVersion, target: ISourceDataConfigurationFormVersion | undefined, newItem: ISourceDataConfigurationFormVersion, targetVersionMetadata?: IFormVersionMetadata) => {
    const sections = targetVersionMetadata?.sections ?? [];

    if (newItem.linked !== (target?.linked ?? true) && (!newItem.sections || Object.keys(newItem.sections).length === 0)  && sections.length > 0) {
        newItem.sections = {};

        for (let valueMetadata of sections) {
            newItem.sections![valueMetadata.id!] = {
                __type: CSharpTypeMapping[ConfigurationType.Section], 
                formSectionMetadataId: valueMetadata.id!,
                linked: true,
                enabled: true
            } as ISourceDataConfigurationFormSection 
        }
    }
}

export const updateSections = (source: ISourceDataConfigurationFormVersion, target: ISourceDataConfigurationFormVersion | undefined, newItem: ISourceDataConfigurationFormVersion, targetVersionMetadata?: IFormVersionMetadata) => {
    if (!newItem.linked) {
        createSections(source, target, newItem, targetVersionMetadata);

        if (source.sections) {
            for (let fieldKey of Object.keys(source.sections)) {
                updateSection(
                    source,
                    target,
                    newItem,
                    fieldKey,
                    targetVersionMetadata
                )
            }
        }
    }
    else if (newItem.linked) {
        newItem.sections = undefined;
    }
}


export const updateSection = (source: ISourceDataConfigurationFormVersion, target: ISourceDataConfigurationFormVersion | undefined,  newItem: ISourceDataConfigurationFormVersion, key: string, targetVersionMetadata?: IFormVersionMetadata) => {
    if (!newItem.sections) {
        return;
    }
    
    let newChildItem = newItem.sections[key];

    
    if (!newChildItem) {
        return;
    }
    
    const {
        __type: currentType, 
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormVersionId: currentVersionId,
        formSectionMetadataId: currentFormSectionMetadataId,
        linked: currentLinked,
        fields: currentFieldCollection,
        ...currentValues
    } = newChildItem as ISourceDataConfigurationFormSection & { __type: string };

    const {
        __type: copyType,
        id: copyId,
        enteredBy: copyEnteredBy,
        enteredDate: copyEnteredDate,
        modifiedBy: copyModifiedBy,
        modifiedDate: copyModifiedDate,
        sourceDataConfigurationFormVersionId: copyVersionId,
        formSectionMetadataId: copyFormSectionMetadataId,
        linked: copyLinked, 
        fields: copyFieldCollection,
        ...copyValues
    } = source.sections![key] as ISourceDataConfigurationFormSection & { __type: string };

    newItem.sections[key] = {
        __type: currentType, 
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormVersionId: currentVersionId,
        formSectionMetadataId: currentFormSectionMetadataId,
        fields: {...currentFieldCollection},
        linked: copyLinked, 
        ...copyValues
    } as ISourceDataConfigurationFormSection

    const targetSectionMetadata = targetVersionMetadata?.sections?.find(y => y.id === key);

    updateSectionFields(source.sections![key], target?.sections?.[key], newItem.sections![key], targetSectionMetadata, targetVersionMetadata);
}

export const createSectionFields = (source: ISourceDataConfigurationFormSection, target: ISourceDataConfigurationFormSection | undefined, newItem: ISourceDataConfigurationFormSection, targetMetadata?: IFormSectionMetadata, targetVersionMetadata?: IFormVersionMetadata) => {
    const fields = targetVersionMetadata?.fields?.filter(x => x.formSectionMetadataId === targetMetadata?.id) ?? [];

    if (newItem.linked !== (target?.linked ?? true) && (!newItem.fields || Object.keys(newItem.fields).length === 0)  && fields.length > 0) {
        newItem.fields = {};

        for (let valueMetadata of fields) {
            newItem.fields![valueMetadata.id!] = {
                __type: CSharpTypeMapping[ConfigurationType.Field], 
                formFieldMetadataId: valueMetadata.id!,
                linked: true,
                enabled: true
            } as ISourceDataConfigurationFormField 
        }
    }
}

export const updateSectionFields = (source: ISourceDataConfigurationFormSection, target: ISourceDataConfigurationFormSection | undefined, newItem: ISourceDataConfigurationFormSection, targetMetadata?: IFormSectionMetadata, targetVersionMetadata?: IFormVersionMetadata) => {
    if (!newItem.linked) {
        createSectionFields(source, target, newItem, targetMetadata, targetVersionMetadata);

        if (source.fields) {
            for (let fieldKey of Object.keys(source.fields)) {
                updateField(
                    source,
                    target,
                    newItem,
                    fieldKey,
                    targetVersionMetadata
                )
            }
        }
    }
    else if (newItem.linked) {
        newItem.fields = undefined;
    }
}



export const updateField = (source: ISourceDataConfigurationFormSection, target: ISourceDataConfigurationFormSection | undefined,  newItem: ISourceDataConfigurationFormSection, key: string, targetVersionMetadata?: IFormVersionMetadata) => {
    if (!newItem.fields) {
        return;
    }

    let newChildItem = newItem.fields[key];

    
    if (!newChildItem) {
        return;
    }
    
    const {
        __type: currentType,
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormVersionId: currentVersionId,
        sourceDataConfigurationFormSectionId: currentSectionId,
        formFieldMetadataId: currentFormFieldMetadataId,
        linked: currentLinked,
        values: currentValueCollection,
        ...currentValues
    } = newChildItem as ISourceDataConfigurationFormField & { __type: string };

    const {
        __type: copyType, 
        id: copyId,
        enteredBy: copyEnteredBy,
        enteredDate: copyEnteredDate,
        modifiedBy: copyModifiedBy,
        modifiedDate: copyModifiedDate,
        sourceDataConfigurationFormVersionId: copyVersionId,
        sourceDataConfigurationFormSectionId: copySectionId,
        formFieldMetadataId: copyFormFieldMetadataId,
        linked: copyLinked, 
        values: copyValueCollection,
        ...copyValues
    } = source.fields![key] as ISourceDataConfigurationFormField & { __type: string };

    newItem.fields[key] = {
        __type: currentType, 
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormVersionId: currentVersionId,
        sourceDataConfigurationFormSectionId: currentSectionId,
        formFieldMetadataId: currentFormFieldMetadataId,
        values: {...currentValueCollection},
        linked: copyLinked, 
        ...copyValues
    } as ISourceDataConfigurationFormField

    const targetFieldMetadata = targetVersionMetadata?.fields?.find(y => y.id === key);

    updateValues(source.fields![key], target?.fields?.[key], newItem.fields![key], targetFieldMetadata);
}

export const createValues = (sourceField: ISourceDataConfigurationFormField, targetField: ISourceDataConfigurationFormField | undefined, newField: ISourceDataConfigurationFormField, targetFieldMetadata?: IFormFieldMetadata) => {
    if (newField.linked !== (targetField?.linked ?? true) && (!newField.values || Object.keys(newField.values).length === 0)  && targetFieldMetadata?.values) {
        newField.values = {};

        for (let valueMetadata of targetFieldMetadata.values) {
            newField.values![valueMetadata.id!] = {
                __type: CSharpTypeMapping[ConfigurationType.Value], 
                formFieldValueMetadataId: valueMetadata.id!,
                linked: true,
                enabled: true
            } as ISourceDataConfigurationFormFieldValue 
        }
    }
}

export const updateValues = (sourceField: ISourceDataConfigurationFormField, targetField: ISourceDataConfigurationFormField | undefined, newField: ISourceDataConfigurationFormField, targetFieldMetadata?: IFormFieldMetadata) => {
    if (!newField.linked) {
        createValues(sourceField, targetField, newField, targetFieldMetadata);

        if (sourceField.values) {
            for (let fieldValueKey of Object.keys(sourceField.values)) {
                updateValue(
                    sourceField,
                    targetField,
                    newField,
                    fieldValueKey
                )
            }
        }
    }
    else if (newField.linked) {
        newField.values = undefined;
    }
}

export const updateValue = (sourceField: ISourceDataConfigurationFormField, targetField: ISourceDataConfigurationFormField | undefined,  newField: ISourceDataConfigurationFormField, fieldValueKey: string) => {
    if (!newField.values) {
        return;
    }
    
    let newFieldValue = newField.values[fieldValueKey];

    if (!newFieldValue) {
        return;
    }
    
    const {
        __type: currentType,
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormFieldId: currentFieldId,
        formFieldValueMetadataId: currentFormFieldValueMetadataId,
        linked: currentLinked, 
        ...currentValues
    } = newFieldValue as ISourceDataConfigurationFormFieldValue & { __type: string };

    const {
        __type: copyType, 
        id: copyId,
        enteredBy: copyEnteredBy,
        enteredDate: copyEnteredDate,
        modifiedBy: copyModifiedBy,
        modifiedDate: copyModifiedDate,
        sourceDataConfigurationFormFieldId: copyFieldId,
        formFieldValueMetadataId: copyFormFieldValueMetadataId,
        linked: copyLinked, 
        ...copyValues
    } = sourceField.values![fieldValueKey] as ISourceDataConfigurationFormFieldValue & { __type: string };

    newField.values[fieldValueKey] = {
        __type: currentType, 
        id: currentId,
        enteredBy: currentEnteredBy,
        enteredDate: currentEnteredDate,
        modifiedBy: currentModifiedBy,
        modifiedDate: currentModifiedDate,
        sourceDataConfigurationFormFieldId: currentFieldId,
        formFieldValueMetadataId: currentFormFieldValueMetadataId,
        linked: copyLinked, 
        ...copyValues
    } as ISourceDataConfigurationFormFieldValue


}



export interface INavItem {
    url?: string;
    target?: string;
    icon: IconDefinition;
    title: string;
    current?: boolean;
    description?: string;
}